<template>
  <q-card
    class="full-width"
    style="max-width: 95vw; height: 90vh; display: flex; flex-direction: column"
  >
    <q-card-section class="row items-center justify-between bg-primary text-white q-py-sm">
      <div class="text-h6">
        Data Pipeline Overrides
        <span v-if="property" class="text-subtitle2 q-ml-sm">
          - {{ property.building_name || property.street_address }}
        </span>
        <span v-else class="text-subtitle2 q-ml-sm">- Global View</span>
      </div>
      <div class="row q-gutter-xs">
        <q-btn icon="refresh" flat round dense no-caps @click="fetchAllData" :loading="loading">
          <q-tooltip>Refresh all data</q-tooltip>
        </q-btn>
        <q-btn icon="close" flat round dense no-caps v-close-popup />
      </div>
    </q-card-section>

    <q-tabs
      v-model="activeTab"
      dense
      class="bg-grey-2 text-grey-7"
      active-color="primary"
      indicator-color="primary"
      align="justify"
      narrow-indicator
      no-caps
    >
      <q-tab v-for="tab in tabs" :key="tab.name" :name="tab.name" :label="tab.label" />
    </q-tabs>

    <q-separator />

    <q-tab-panels v-model="activeTab" animated class="col shadow-2 overflow-auto">
      <q-tab-panel v-for="tab in tabs" :key="tab.name" :name="tab.name" class="q-pa-none">
        <div class="q-pa-md">
          <div class="flex justify-between items-start q-gutter-md q-mb-sm">
            <div>
              <div class="text-subtitle1 q-mb-xs">{{ tab.label }}</div>
              <div class="text-caption text-grey-7" style="max-width: 600px">
                {{ getTabDescription(tab.name) }}
              </div>
            </div>

            <div class="row items-center q-gutter-sm" style="flex-shrink: 0">
              <q-input
                v-model="searchQuery"
                placeholder="Search..."
                outlined
                dense
                style="min-width: 250px"
                clearable
              >
                <template #append>
                  <q-icon name="search" />
                </template>
              </q-input>
              <q-btn
                v-if="canEdit && tab.name !== 'proposed_edits'"
                color="positive"
                icon="add"
                label="Add Entry"
                dense
                no-caps
                @click="openAddDialog(tab.name)"
              />
              <q-btn
                v-else-if="tab.name !== 'proposed_edits'"
                color="positive"
                icon="add"
                label="Propose Entry"
                dense
                no-caps
                @click="openAddDialog(tab.name)"
              />
            </div>
          </div>
        </div>

        <q-table
          v-if="tab.name !== 'do_merge_addresses' && tab.name !== 'do_not_merge'"
          :rows="filteredRows(tab.name)"
          :columns="getColumns(tab.name)"
          :row-key="getRowKey(tab.name)"
          flat
          bordered
          dense
          :rows-per-page-options="[10, 20, 50, 0]"
          :loading="loading"
          :pagination="{ sortBy: 'created_on', descending: true, rowsPerPage: 10 }"
        >
          <template #body-cell-actions="props">
            <q-td :props="props" class="q-gutter-xs text-center">
              <template v-if="tab.name === 'proposed_edits'">
                <q-btn
                  v-if="canEdit"
                  color="positive"
                  icon="check"
                  flat
                  dense
                  no-caps
                  size="sm"
                  @click="approveProposal(props.row.id)"
                >
                  <q-tooltip>Approve Proposal</q-tooltip>
                </q-btn>
                <q-btn
                  color="primary"
                  icon="edit"
                  flat
                  dense
                  no-caps
                  size="sm"
                  @click="openEditDialog(props.row)"
                >
                  <q-tooltip>Edit Proposal</q-tooltip>
                </q-btn>
                <q-btn
                  color="negative"
                  icon="delete"
                  flat
                  dense
                  no-caps
                  size="sm"
                  @click="confirmDeleteProposal(props.row)"
                >
                  <q-tooltip>Delete Proposal</q-tooltip>
                </q-btn>
              </template>
              <template v-else>
                <q-btn
                  color="primary"
                  icon="edit"
                  flat
                  dense
                  no-caps
                  size="sm"
                  @click="openEditDialog(props.row)"
                >
                  <q-tooltip>{{ canEdit ? 'Edit' : 'Propose Edit' }}</q-tooltip>
                </q-btn>
                <q-btn
                  color="negative"
                  icon="delete"
                  flat
                  dense
                  no-caps
                  size="sm"
                  @click="confirmDelete(props.row)"
                >
                  <q-tooltip>{{ canEdit ? 'Delete' : 'Propose Deletion' }}</q-tooltip>
                </q-btn>
              </template>
            </q-td>
          </template>
          <template #body-cell-data_summary="props">
            <q-td :props="props">
              <div class="ellipsis" style="max-width: 300px">
                {{ String(props.value) }}
              </div>
              <q-tooltip max-width="400px" style="word-break: break-all">
                {{ String(props.value) }}
              </q-tooltip>
            </q-td>
          </template>
        </q-table>

        <!-- Custom grouped table for do_not_merge -->
        <div v-if="tab.name === 'do_not_merge'" class="q-pa-sm">
          <div v-if="loading" class="text-center q-pa-md">
            <q-spinner size="lg" />
          </div>
          <div v-else-if="groupedDoNotMerge.length === 0" class="text-center text-grey q-pa-md">
            No do-not-merge groups found
          </div>
          <div v-else class="merge-groups-container">
            <div
              v-for="(group, groupIndex) in groupedDoNotMerge"
              :key="group.groupId"
              class="merge-group q-mb-md"
              :class="
                groupIndex % 2 === 0
                  ? $q.dark.isActive
                    ? 'bg-grey-9'
                    : 'bg-grey-1'
                  : $q.dark.isActive
                    ? 'bg-deep-orange-9'
                    : 'bg-orange-1'
              "
            >
              <div
                class="merge-group-header q-pa-sm text-caption row items-center justify-between"
                :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'"
              >
                <span
                  >Group {{ groupIndex + 1 }} ({{ group.addresses.length }} addresses,
                  {{ group.pairs.length }} pairs)</span
                >
                <span class="text-caption"
                  >Created {{ formatDate(group.pairs[0]?.created_on) }} by
                  {{ group.pairs[0]?.created_by }}</span
                >
              </div>
              <div class="q-pa-sm">
                <div
                  class="text-caption q-mb-xs"
                  :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'"
                >
                  Addresses in this group:
                </div>
                <q-chip
                  v-for="addr in group.addresses"
                  :key="addr"
                  dense
                  size="sm"
                  class="q-mr-xs q-mb-xs"
                  >{{ addr }}</q-chip
                >
              </div>
              <q-separator />
              <q-list dense separator>
                <q-item
                  v-for="pair in group.pairs"
                  :key="`${pair.address1}-${pair.address2}`"
                  class="q-py-xs"
                >
                  <q-item-section>
                    <q-item-label class="row items-center q-gutter-sm">
                      <span>{{ pair.address1 }}</span>
                      <q-icon name="block" color="negative" size="xs" />
                      <span>{{ pair.address2 }}</span>
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <div class="row q-gutter-xs">
                      <q-btn
                        color="primary"
                        icon="edit"
                        flat
                        dense
                        no-caps
                        size="sm"
                        @click="openEditDialog(pair)"
                      >
                        <q-tooltip>{{ canEdit ? 'Edit' : 'Propose Edit' }}</q-tooltip>
                      </q-btn>
                      <q-btn
                        color="negative"
                        icon="delete"
                        flat
                        dense
                        no-caps
                        size="sm"
                        @click="confirmDelete(pair)"
                      >
                        <q-tooltip>{{ canEdit ? 'Delete' : 'Propose Deletion' }}</q-tooltip>
                      </q-btn>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>
        </div>

        <!-- Custom grouped table for do_merge_addresses -->
        <div v-if="tab.name === 'do_merge_addresses'" class="q-pa-sm">
          <div v-if="loading" class="text-center q-pa-md">
            <q-spinner size="lg" />
          </div>
          <div v-else-if="groupedMergeAddresses.length === 0" class="text-center text-grey q-pa-md">
            No merge address groups found
          </div>
          <div v-else class="merge-groups-container">
            <div
              v-for="(group, groupIndex) in groupedMergeAddresses"
              :key="group.groupId"
              class="merge-group q-mb-md"
              :class="
                groupIndex % 2 === 0
                  ? $q.dark.isActive
                    ? 'bg-grey-9'
                    : 'bg-grey-1'
                  : $q.dark.isActive
                    ? 'bg-blue-grey-9'
                    : 'bg-blue-1'
              "
            >
              <div
                class="merge-group-header q-pa-sm text-caption row items-center justify-between"
                :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-7'"
              >
                <span>Group {{ groupIndex + 1 }} ({{ group.addresses.length }} addresses)</span>
                <span class="text-caption"
                  >Created {{ formatDate(group.addresses[0]?.created_on) }} by
                  {{ group.addresses[0]?.created_by }}</span
                >
              </div>
              <q-list dense separator>
                <q-item v-for="addr in group.addresses" :key="addr.address" class="q-py-xs">
                  <q-item-section>
                    <q-item-label>{{ addr.address }}</q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <div class="row q-gutter-xs">
                      <q-btn
                        color="primary"
                        icon="edit"
                        flat
                        dense
                        no-caps
                        size="sm"
                        @click="openEditDialog(addr)"
                      >
                        <q-tooltip>{{ canEdit ? 'Edit' : 'Propose Edit' }}</q-tooltip>
                      </q-btn>
                      <q-btn
                        color="negative"
                        icon="delete"
                        flat
                        dense
                        no-caps
                        size="sm"
                        @click="confirmDelete(addr)"
                      >
                        <q-tooltip>{{ canEdit ? 'Delete' : 'Propose Deletion' }}</q-tooltip>
                      </q-btn>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>
        </div>
      </q-tab-panel>
    </q-tab-panels>

    <!-- Add/Edit Dialog -->
    <q-dialog v-model="showFormDialog" persistent>
      <q-card style="min-width: 500px; max-width: 900px">
        <q-card-section class="row items-center justify-between">
          <div class="text-h6">{{ isEditing ? 'Edit' : 'Add' }} {{ currentTabLabel }}</div>
          <q-btn icon="close" flat round dense no-caps v-close-popup />
        </q-card-section>

        <q-card-section class="q-pt-none q-gutter-sm">
          <q-banner v-if="formErrors.length > 0" class="bg-negative text-white q-mb-md" dense>
            <template #avatar>
              <q-icon name="error" color="white" />
            </template>
            <div class="text-weight-medium q-mb-xs">Please fix the following errors:</div>
            <ul class="q-ma-none q-pl-md" style="margin-top: 4px">
              <li v-for="error in formErrors" :key="error">{{ error }}</li>
            </ul>
          </q-banner>
          <div v-if="activeTab === 'do_not_merge' && !isEditing" class="q-mb-md">
            <q-checkbox v-model="isBulkMode" label="Bulk Pair Creation">
              <q-tooltip>
                Enter a list of items in Side A and Side B to create do-not-merge pairs for every
                combination.
              </q-tooltip>
            </q-checkbox>
          </div>

          <div v-if="activeTab === 'do_merge_addresses' && !isEditing" class="q-mb-md">
            <div class="text-caption text-grey-7">
              Enter addresses (one per line) to group them together. All addresses will be merged
              under a single group.
            </div>
          </div>

          <div v-if="activeTab === 'allowed_website_domains'" class="q-mb-md">
            <q-banner class="bg-info text-white" dense>
              <template #avatar>
                <q-icon name="info" color="white" />
              </template>
              <div class="text-weight-medium q-mb-xs">Using Wildcard Patterns</div>
              <div class="text-caption">
                Domain patterns use the <strong>%</strong> wildcard character for flexible matching
                in BigQuery LIKE queries. The <strong>%</strong> matches any sequence of characters
                (including none).
              </div>
              <div class="text-caption q-mt-sm">
                <strong>Examples:</strong>
                <ul class="q-ma-none q-pl-md q-mt-xs">
                  <li>
                    <code>%example.com</code> matches "example.com", "www.example.com",
                    "subdomain.example.com"
                  </li>
                  <li>
                    <code>example.com%</code> matches "example.com", "example.com/page",
                    "example.com/path/to/page"
                  </li>
                  <li><code>%example%</code> matches any URL containing "example"</li>
                </ul>
              </div>
            </q-banner>
          </div>

          <template v-if="isBulkMode && activeTab === 'do_not_merge' && !isEditing">
            <div class="row q-gutter-sm">
              <q-input
                v-model="bulkSideA"
                label="Side A (one per line)"
                type="textarea"
                outlined
                dense
                class="col"
                placeholder="Address or ID..."
              />
              <q-input
                v-model="bulkSideB"
                label="Side B (one per line)"
                type="textarea"
                outlined
                dense
                class="col"
                placeholder="Address or ID..."
              />
            </div>
          </template>

          <template v-else-if="activeTab === 'do_merge_addresses' && !isEditing">
            <q-input
              v-model="bulkSideA"
              label="Addresses (one per line)"
              type="textarea"
              outlined
              dense
              rows="6"
              placeholder="Enter addresses to merge into a new group..."
            />
          </template>

          <template v-else>
            <div v-for="field in getFields(activeTab)" :key="field.name">
              <div :class="field.previewTrigger ? 'row items-center q-gutter-sm' : ''">
                <q-input
                  v-if="field.type === 'string' || field.type === 'number'"
                  v-model="form[field.name]"
                  :label="field.label"
                  :type="field.type === 'number' ? 'number' : 'text'"
                  outlined
                  dense
                  :class="field.previewTrigger ? 'col' : 'q-mb-sm'"
                  :error="fieldHasError(field.name)"
                  :error-message="getFieldError(field.name)"
                />
                <q-btn
                  v-if="field.previewTrigger"
                  color="primary"
                  label="Autofill"
                  dense
                  no-caps
                  @click="previewProperty(form[field.name])"
                  :loading="previewLoading"
                  class="q-px-sm q-mb-sm"
                />
                <div v-else-if="field.type === 'boolean'" class="q-mb-sm">
                  <q-checkbox v-model="form[field.name]" :label="field.label">
                    <q-tooltip v-if="field.tooltip" max-width="250px">
                      {{ field.tooltip }}
                    </q-tooltip>
                  </q-checkbox>
                </div>
                <q-select
                  v-else-if="field.type === 'select'"
                  v-model="form[field.name]"
                  :label="field.label"
                  :options="field.options"
                  outlined
                  dense
                  class="q-mb-sm"
                  :error="fieldHasError(field.name)"
                  :error-message="getFieldError(field.name)"
                />
                <q-select
                  v-else-if="field.type === 'array'"
                  v-model="form[field.name]"
                  :label="field.label"
                  use-input
                  use-chips
                  multiple
                  hide-dropdown-icon
                  input-debounce="0"
                  new-value-mode="add-unique"
                  outlined
                  dense
                  class="q-mb-sm"
                />
              </div>
            </div>
          </template>

          <div
            v-if="activeTab === 'do_not_merge' && property && sourcesTableData.length > 0"
            class="q-mt-lg"
          >
            <q-separator class="q-mb-md" />
            <div class="text-subtitle2 q-mb-sm">Sources for this property</div>
            <q-table
              :rows="sourcesTableData"
              :columns="sourcesTableColumns"
              row-key="listing_url"
              flat
              bordered
              dense
              :rows-per-page-options="[0]"
              hide-bottom
            >
              <template #body-cell-listing_url="props">
                <q-td :props="props">
                  <div class="ellipsis" style="max-width: 400px">
                    <a
                      :href="props.value"
                      target="_blank"
                      class="text-primary"
                      style="text-decoration: none"
                      v-if="props.value && props.value.startsWith('http')"
                    >
                      {{ props.value }}
                    </a>
                    <span v-else>{{ props.value }}</span>
                  </div>
                  <q-tooltip v-if="props.value">{{ props.value }}</q-tooltip>
                </q-td>
              </template>
            </q-table>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="text-primary">
          <q-btn flat label="Cancel" dense no-caps @click="clearFormErrors" v-close-popup />
          <q-btn color="primary" :label="submitButtonLabel" dense no-caps @click="saveEntry" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Delete Confirmation -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="warning" text-color="white" />
          <span class="q-ml-sm">Are you sure you want to delete this entry?</span>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="primary" dense no-caps v-close-popup />
          <q-btn
            flat
            :label="deleteButtonLabel"
            color="negative"
            dense
            no-caps
            @click="deleteEntry"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import type { PropertyDetailsResponse, DebugProperty, BuildingSourceEntry } from 'common'
import propertyApi from '@/api/property'
import { useUserStore } from '@/stores/user'
import { useDataPipelineManualEditsStore } from '@/stores/datapipelinemanualedits'
import type { QTableColumn } from 'quasar'

import type {
  DoNotMerge,
  ExcludedUrl,
  PrioritizeListing,
  AddressRename,
  AllowedWebsiteDomain,
  BuildingOverride,
  DoMergeAddress,
  SightmapUrl,
  ProposedEdit,
  TableName,
  BaseManualFix
} from '@/logic/datapipelinemanualfixes.logic'

interface Tab {
  name: TableName
  label: string
}

interface Field {
  name: string
  label: string
  type: 'string' | 'number' | 'boolean' | 'select' | 'array'
  required?: boolean
  options?: string[]
  tooltip?: string
  previewTrigger?: boolean
  textarea?: boolean
}

interface SourceRow {
  listing_url: string
  building_name: string
  street_address: string
}

export default defineComponent({
  name: 'DataPipelineOverridesManager',
  props: {
    property: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    debugProperties: {
      type: Array as PropType<DebugProperty[]>,
      default: () => []
    }
  },
  emits: ['refresh'],
  data() {
    return {
      tabs: [
        { name: 'do_not_merge', label: 'Do Not Merge Overrides' },
        { name: 'do_merge_addresses', label: 'Do Merge Addresses Overrides' },
        { name: 'excluded_urls', label: 'Excluded URLs' },
        { name: 'prioritize_listing', label: 'Prioritize Listing Overrides' },
        { name: 'address_rename', label: 'Address Rename Overrides' },
        { name: 'allowed_website_domains', label: 'Allowed Domains Overrides' },
        { name: 'building_overrides', label: 'Building Overrides' },
        { name: 'sightmap_urls', label: 'Add a Sightmap URL' },
        { name: 'proposed_edits', label: 'Pending Proposals' }
      ] as Tab[],
      activeTab: 'do_not_merge' as TableName,
      showFormDialog: false,
      showDeleteDialog: false,
      isEditing: false,
      isBulkMode: false,
      bulkSideA: '',
      bulkSideB: '',
      editingRow: null as any,
      deletingRow: null as any,
      form: {} as Record<string, any>,
      previewLoading: false,
      formErrors: [] as string[],
      fieldErrors: {} as Record<string, string>,
      searchQuery: '',
      sourcesTableColumns: [
        {
          name: 'listing_url',
          label: 'Listing URL',
          field: 'listing_url',
          align: 'left' as const,
          sortable: true
        },
        {
          name: 'building_name',
          label: 'Building Name',
          field: 'building_name',
          align: 'left' as const,
          sortable: true
        },
        {
          name: 'street_address',
          label: 'Street Address',
          field: 'street_address',
          align: 'left' as const,
          sortable: true
        }
      ],
      userStore: useUserStore(),
      manualEditsStore: useDataPipelineManualEditsStore()
    }
  },
  computed: {
    loading(): boolean {
      return this.manualEditsStore.loading
    },
    canEdit(): boolean {
      return this.manualEditsStore.canEdit
    },
    allData(): Record<TableName, BaseManualFix[]> {
      return this.manualEditsStore.allData
    },
    submitButtonLabel() {
      if (this.activeTab === 'proposed_edits') {
        return this.isEditing ? 'Save' : 'Add'
      }
      return this.isEditing ? (this.canEdit ? 'Save' : 'Propose') : this.canEdit ? 'Add' : 'Propose'
    },
    deleteButtonLabel() {
      if (this.activeTab === 'proposed_edits') {
        return 'Delete'
      }
      return this.canEdit ? 'Delete' : 'Propose Deletion'
    },
    currentTabLabel() {
      return this.tabs.find((t) => t.name === this.activeTab)?.label || ''
    },
    propertyAddresses(): string[] {
      if (!this.property) {
        return []
      }

      const addresses = new Set<string>()

      // From the property record
      if (this.property.street_address) {
        addresses.add(this.property.street_address.toLowerCase().trim())
      }
      if (Array.isArray(this.property.street_address_alias)) {
        this.property.street_address_alias.forEach((a: string) => {
          if (a) addresses.add(a.toLowerCase().trim())
        })
      }

      // From manual building overrides
      const overrides = (this.allData['building_overrides'] || []) as BuildingOverride[]
      const propOverride = overrides.find((o) => o.id === this.property?.id)
      if (propOverride) {
        if (propOverride.street_address) {
          addresses.add(propOverride.street_address.toLowerCase().trim())
        }
        if (Array.isArray(propOverride.street_address_alias)) {
          propOverride.street_address_alias.forEach((a: string) => {
            if (a) addresses.add(a.toLowerCase().trim())
          })
        }
      }

      return Array.from(addresses).filter(Boolean)
    },
    groupedMergeAddresses() {
      // Get rows with property filter but without search filter applied
      const allRows = (this.allData['do_merge_addresses'] || []) as DoMergeAddress[]
      let rowsToGroup = allRows

      if (this.property) {
        const searchAddresses = this.propertyAddresses
        const matchingGroupIds = new Set<string>()

        for (const row of allRows) {
          const rowAddr = (row.address || '').toLowerCase().trim()
          if (rowAddr && searchAddresses.includes(rowAddr)) {
            if (row.merge_group_id) {
              matchingGroupIds.add(row.merge_group_id)
            }
          }
        }

        rowsToGroup = allRows.filter(
          (row) => row.merge_group_id && matchingGroupIds.has(row.merge_group_id)
        )
      }

      // Group rows
      const groupMap = new Map<string, DoMergeAddress[]>()
      for (const row of rowsToGroup) {
        const groupId = row.merge_group_id || 'ungrouped'
        if (!groupMap.has(groupId)) {
          groupMap.set(groupId, [])
        }
        groupMap.get(groupId)!.push(row)
      }

      // Convert to groups
      const groups = Array.from(groupMap.entries())
        .map(([groupId, addresses]) => ({
          groupId,
          addresses: addresses.sort((a, b) => (a.address || '').localeCompare(b.address || ''))
        }))
        .sort((a, b) => {
          const aMaxDate = Math.max(
            ...a.addresses.map((addr) =>
              addr.created_on ? new Date(addr.created_on).getTime() : 0
            )
          )
          const bMaxDate = Math.max(
            ...b.addresses.map((addr) =>
              addr.created_on ? new Date(addr.created_on).getTime() : 0
            )
          )
          return bMaxDate - aMaxDate
        })

      // If search query exists, filter groups where any address matches
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        return groups.filter((group) => {
          return group.addresses.some((addr) => {
            for (const val of Object.values(addr)) {
              if (val === null || val === undefined) continue
              const strVal = (
                typeof val === 'object' ? JSON.stringify(val) : (val as any).toString()
              ).toLowerCase()
              if (strVal.includes(query)) {
                return true
              }
            }
            return false
          })
        })
      }

      return groups
    },
    groupedDoNotMerge() {
      // Get rows with property filter but without search filter applied
      const allRows = (this.allData['do_not_merge'] || []) as DoNotMerge[]
      let rowsToGroup = allRows

      if (this.property) {
        const propId = this.property.id
        const searchAddresses = this.propertyAddresses

        const matchingGroupRoots = new Set<string>()

        // Union-Find setup
        const parent = new Map<string, string>()
        const find = (x: string): string => {
          if (!parent.has(x)) {
            parent.set(x, x)
          }
          if (parent.get(x) !== x) {
            parent.set(x, find(parent.get(x)!))
          }
          return parent.get(x)!
        }
        const union = (a: string, b: string) => {
          const rootA = find(a)
          const rootB = find(b)
          if (rootA !== rootB) {
            parent.set(rootA, rootB)
          }
        }

        // Build Union-Find
        for (const row of allRows) {
          const addr1 = (row.address1 || '').toLowerCase().trim()
          const addr2 = (row.address2 || '').toLowerCase().trim()
          if (addr1 && addr2) {
            union(addr1, addr2)
          }
        }

        // Find which roots match our building
        for (const row of allRows) {
          const addr1 = (row.address1 || '').toLowerCase().trim()
          const addr2 = (row.address2 || '').toLowerCase().trim()
          const root = addr1 ? find(addr1) : addr2 ? find(addr2) : null

          if (
            row.id === propId ||
            (addr1 && searchAddresses.includes(addr1)) ||
            (addr2 && searchAddresses.includes(addr2))
          ) {
            if (root) {
              matchingGroupRoots.add(root)
            }
          }
        }

        rowsToGroup = allRows.filter((row) => {
          const addr1 = (row.address1 || '').toLowerCase().trim()
          const addr2 = (row.address2 || '').toLowerCase().trim()
          const root = addr1 ? find(addr1) : addr2 ? find(addr2) : null
          return row.id === propId || (root && matchingGroupRoots.has(root))
        })
      }

      if (rowsToGroup.length === 0) {
        return []
      }

      // Final grouping for display
      const parent = new Map<string, string>()
      const find = (x: string): string => {
        if (!parent.has(x)) {
          parent.set(x, x)
        }
        if (parent.get(x) !== x) {
          parent.set(x, find(parent.get(x)!))
        }
        return parent.get(x)!
      }
      const union = (a: string, b: string) => {
        const rootA = find(a)
        const rootB = find(b)
        if (rootA !== rootB) {
          parent.set(rootA, rootB)
        }
      }

      for (const row of rowsToGroup) {
        const addr1 = (row.address1 || '').toLowerCase().trim()
        const addr2 = (row.address2 || '').toLowerCase().trim()
        if (addr1 && addr2) {
          union(addr1, addr2)
        }
      }

      const groupMap = new Map<string, DoNotMerge[]>()
      for (const row of rowsToGroup) {
        const addr1 = (row.address1 || '').toLowerCase().trim()
        const addr2 = (row.address2 || '').toLowerCase().trim()
        const root = addr1 ? find(addr1) : addr2 ? find(addr2) : 'ungrouped'

        if (!groupMap.has(root)) {
          groupMap.set(root, [])
        }
        groupMap.get(root)!.push(row)
      }

      // Collect unique addresses for each group
      const groups = Array.from(groupMap.entries())
        .map(([root, pairs]) => {
          const addressSet = new Set<string>()
          for (const pair of pairs) {
            if (pair.address1) {
              addressSet.add(pair.address1)
            }
            if (pair.address2) {
              addressSet.add(pair.address2)
            }
          }
          return {
            groupId: root,
            pairs,
            addresses: Array.from(addressSet).sort()
          }
        })
        .sort((a, b) => {
          const aMaxDate = Math.max(
            ...a.pairs.map((pair) => (pair.created_on ? new Date(pair.created_on).getTime() : 0))
          )
          const bMaxDate = Math.max(
            ...b.pairs.map((pair) => (pair.created_on ? new Date(pair.created_on).getTime() : 0))
          )
          return bMaxDate - aMaxDate
        })

      // If search query exists, filter groups where any pair matches
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        return groups.filter((group) => {
          return group.pairs.some((pair) => {
            for (const val of Object.values(pair)) {
              if (val === null || val === undefined) {
                continue
              }
              const strVal = (
                typeof val === 'object' ? JSON.stringify(val) : (val as any).toString()
              ).toLowerCase()
              if (strVal.includes(query)) {
                return true
              }
            }
            return false
          })
        })
      }

      return groups
    },
    sourcesTableData(): SourceRow[] {
      if (!this.property) {
        return []
      }

      const propId = this.property.id
      const debugProp = this.debugProperties.find((p) => p.id === propId)
      if (!debugProp) {
        return []
      }

      const urlToNames = new Map<string, Set<string>>()
      const urlToAddresses = new Map<string, Set<string>>()

      const valueList = (val: unknown): string[] => {
        if (!val) {
          return []
        }
        if (Array.isArray(val)) {
          return val as string[]
        }
        if (typeof val === 'string') {
          try {
            const parsed = JSON.parse(val)
            if (Array.isArray(parsed)) {
              return parsed as string[]
            }
          } catch {
            // Not JSON
          }
          const splitters = [',', ';', '|']
          for (const s of splitters) {
            if (val.includes(s)) {
              return val
                .split(s)
                .map((v: string) => v.trim())
                .filter(Boolean)
            }
          }
          return [val]
        }
        return [typeof val === 'object' ? JSON.stringify(val) : (val as any).toString()]
      }

      const processEntry = (entry: BuildingSourceEntry, targetMap: Map<string, Set<string>>) => {
        const { sources_url, value } = entry
        const add = (url: string, val: string) => {
          if (!url || typeof url !== 'string') {
            return
          }
          if (!targetMap.has(url)) {
            targetMap.set(url, new Set())
          }
          if (val) {
            const vals = valueList(val)
            for (const v of vals) {
              targetMap.get(url)!.add(v)
            }
          }
        }

        if (Array.isArray(sources_url)) {
          for (const url of sources_url) {
            add(url, value as string)
          }
        } else if (sources_url && typeof sources_url === 'object') {
          for (const [val, urls] of Object.entries(sources_url)) {
            const urlList = Array.isArray(urls) ? urls : ([urls] as string[])
            for (const url of urlList) {
              add(url, val)
            }
          }
        }
      }

      if (debugProp.building_source) {
        for (const entry of debugProp.building_source) {
          if (entry.attribute === 'building_name' || entry.attribute === 'building_name_alias') {
            processEntry(entry, urlToNames)
          } else if (
            entry.attribute === 'street_address' ||
            entry.attribute === 'street_address_alias'
          ) {
            processEntry(entry, urlToAddresses)
          }
        }
      }

      const intermediateRows: SourceRow[] = []
      const allUrls = new Set([
        ...(debugProp.listing_urls || []),
        ...urlToNames.keys(),
        ...urlToAddresses.keys()
      ])

      for (const url of allUrls) {
        const names = urlToNames.has(url) ? Array.from(urlToNames.get(url)!) : ['']
        const addresses = urlToAddresses.has(url) ? Array.from(urlToAddresses.get(url)!) : ['']

        for (const name of names) {
          for (const address of addresses) {
            intermediateRows.push({
              listing_url: url,
              building_name: name,
              street_address: address
            })
          }
        }
      }

      const finalRowsMap = new Map<string, SourceRow>()
      const addressesWithNames = new Set<string>()

      // First pass: identify addresses that have at least one non-null building name
      for (const row of intermediateRows) {
        if (row.building_name) {
          addressesWithNames.add((row.street_address || '').trim().toLowerCase())
        }
      }

      // Second pass: filter and deduplicate by building_name and street_address
      for (const row of intermediateRows) {
        const addr = (row.street_address || '').trim().toLowerCase()
        const name = (row.building_name || '').trim().toLowerCase()

        // If this address has a row with a name, skip rows for this address that don't have a name
        if (addressesWithNames.has(addr) && !row.building_name) {
          continue
        }

        const dedupKey = `${addr}|${name}`
        if (!finalRowsMap.has(dedupKey)) {
          finalRowsMap.set(dedupKey, row)
        }
      }

      return Array.from(finalRowsMap.values()).sort((a, b) =>
        (a.street_address || '').localeCompare(b.street_address || '')
      )
    }
  },
  watch: {
    activeTab() {
      this.searchQuery = ''
    },
    // Watch form changes to clear field-specific errors as user types
    form: {
      handler(newForm) {
        if (this.fieldErrors && Object.keys(this.fieldErrors).length > 0) {
          // Clear errors for fields that now have values
          for (const fieldName in this.fieldErrors) {
            const value = newForm[fieldName]
            if (
              value !== null &&
              value !== undefined &&
              (typeof value !== 'string' || value.trim() !== '')
            ) {
              const errorMessage = this.fieldErrors[fieldName]
              delete this.fieldErrors[fieldName]
              // Also remove from the formErrors array
              this.formErrors = this.formErrors.filter((err) => err !== errorMessage)
            }
          }
        }
      },
      deep: true
    }
  },
  mounted() {
    void this.fetchAllData()
  },
  methods: {
    getRowKey(tableName: TableName): string | ((row: BaseManualFix) => string) {
      switch (tableName) {
        case 'proposed_edits': {
          return 'id'
        }
        case 'do_not_merge':
        case 'address_rename':
        case 'allowed_website_domains':
        case 'building_overrides': {
          return 'id'
        }
        case 'excluded_urls': {
          return 'url'
        }
        case 'prioritize_listing': {
          return (row: BaseManualFix) => {
            const r = row as PrioritizeListing
            return `${r.building_id}_${r.listing_source}`
          }
        }
        case 'do_merge_addresses': {
          return (row: BaseManualFix) => {
            const r = row as DoMergeAddress
            return `${r.merge_group_id}_${r.address}`
          }
        }
        case 'sightmap_urls': {
          return 'widget_hash_id'
        }
        default: {
          return 'id'
        }
      }
    },
    getApiResourceName(tableName: TableName): string {
      return this.manualEditsStore.getApiResourceName(tableName)
    },
    async fetchTabData(tableName: TableName): Promise<void> {
      try {
        await this.manualEditsStore.fetchTabData(tableName)
      } catch (error) {
        console.error(`Error fetching data for ${tableName}:`, error)
        this.$q.notify({
          type: 'negative',
          message: `Failed to fetch ${tableName} data`
        })
      }
    },
    async fetchAllData() {
      try {
        await this.manualEditsStore.fetchAllData(this.tabs)
      } catch (error) {
        console.error('Error fetching data pipeline overrides:', error)
        this.$q.notify({ type: 'negative', message: 'Failed to fetch overrides' })
      }
    },
    async previewProperty(buildingId: string) {
      if (!buildingId) {
        this.$q.notify({ type: 'warning', message: 'Please enter a Building ID' })
        return
      }
      this.previewLoading = true
      try {
        const details = await propertyApi.details(buildingId)
        if (details) {
          this.form.building_name = details.building_name
          this.form.street_address = details.street_address
          this.form.city = details.city
          this.form.state = details.state
          this.form.zip_code = details.zip_code
          this.form.lat = details.lat
          this.form.lon = details.lon
          this.$q.notify({ type: 'positive', message: 'Property details autofilled successfully' })
        }
      } catch (error) {
        console.error('Error fetching property details:', error)
        this.$q.notify({ type: 'negative', message: 'Failed to autofill property details' })
      } finally {
        this.previewLoading = false
      }
    },
    getTabDescription(tabName: TableName) {
      switch (tabName) {
        case 'do_not_merge': {
          return 'Prevents two addresses from being merged into the same building record. Use this when addresses are similar but represent distinct properties.'
        }
        case 'do_merge_addresses': {
          return 'Forces addresses in a group to merge into a single building record (as long as they are within one mile of each other). Use this to consolidate listings that should be part of the same property.'
        }
        case 'excluded_urls': {
          return 'Removes specific URLs from the data pipeline. Use this to exclude unreliable or problematic listing sources from being processed.'
        }
        case 'prioritize_listing': {
          return 'Makes data from a specific listing source take precedence over other sources when determining unit availability and pricing. Use this when one source is more accurate than others for a property.'
        }
        case 'address_rename': {
          return 'Changes the address of all listings that exactly match the specified pattern. Use this to standardize addresses across different listing sources.'
        }
        case 'allowed_website_domains': {
          return 'Expands the domains we scrape for concessions and building websites. Use wildcard patterns (%) to match multiple URLs from the same domain.'
        }
        case 'building_overrides': {
          return 'Forces updates to building-level data (buildings are collections of listings). When "Force Override" is enabled, the value always updates; when disabled, it only updates if the current value is null.'
        }
        case 'sightmap_urls': {
          return 'Enables Sightmap scraping for a property by providing the Sightmap widget hash ID.'
        }
        case 'proposed_edits': {
          return 'Proposed changes from all users. Authorized users can approve these to make them live edits.'
        }
        default: {
          return ''
        }
      }
    },
    formatDate(val: string) {
      if (!val) {
        return ''
      }
      try {
        const date = new Date(val)
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return val
      }
    },
    clearFormErrors() {
      this.formErrors = []
      this.fieldErrors = {}
    },
    fieldHasError(fieldName: string) {
      return fieldName in this.fieldErrors
    },
    getFieldError(fieldName: string) {
      return this.fieldErrors[fieldName] || ''
    },
    filteredRows(tableName: TableName): BaseManualFix[] {
      let rows = this.allData[tableName] || []

      // Only show sightmap rows that have lat/lon (filter out soft-deleted ones)
      if (tableName === 'sightmap_urls') {
        rows = (rows as SightmapUrl[]).filter((r) => r.lat !== null && r.lon !== null)
      }

      if (this.property) {
        const propId = this.property.id
        const searchAddresses = this.propertyAddresses
        const website = (this.property.building_website || '').toLowerCase()
        const debugProp = this.debugProperties.find((p) => p.id === propId)
        const listingUrls = debugProp?.listing_urls || []

        rows = rows.filter((row: BaseManualFix) => {
          switch (tableName) {
            case 'do_not_merge': {
              const r = row as DoNotMerge
              if (r.id === propId) {
                return true
              }
              if (r.address1 && searchAddresses.includes(r.address1.toLowerCase().trim())) {
                return true
              }
              if (r.address2 && searchAddresses.includes(r.address2.toLowerCase().trim())) {
                return true
              }
              return false
            }
            case 'excluded_urls': {
              const r = row as ExcludedUrl
              return r.url && listingUrls.includes(r.url)
            }
            case 'prioritize_listing': {
              const r = row as PrioritizeListing
              return r.building_id === propId
            }
            case 'address_rename': {
              const r = row as AddressRename
              if (r.id === propId) {
                return true
              }
              if (
                r.address_pattern &&
                searchAddresses.some((a: string) =>
                  a.includes(r.address_pattern!.toLowerCase().trim())
                )
              ) {
                return true
              }
              return false
            }
            case 'allowed_website_domains': {
              const r = row as AllowedWebsiteDomain
              if (!r.domain_pattern || !website) return false
              const escaped = r.domain_pattern
                .toLowerCase()
                .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                .replace(/%/g, '.*')
              const regex = new RegExp(`^${escaped}$`)
              return regex.test(website)
            }
            case 'building_overrides': {
              const r = row as BuildingOverride
              return r.id === propId
            }
            case 'do_merge_addresses': {
              const r = row as DoMergeAddress
              return r.address && searchAddresses.includes(r.address.toLowerCase().trim())
            }
            case 'sightmap_urls': {
              const r = row as SightmapUrl
              return r.widget_hash_id && listingUrls.includes(r.widget_hash_id)
            }
            case 'proposed_edits': {
              // Proposals are global for now, or we could filter by building if the data contains building_id
              return true
            }
            default: {
              return false
            }
          }
        })
      }

      if (tableName === 'proposed_edits') {
        // Sort proposals by created_on descending
        return [...(rows as ProposedEdit[])].sort((a, b) => {
          return new Date(b.created_on || 0).getTime() - new Date(a.created_on || 0).getTime()
        })
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        const scoredRows = rows
          .map((row: BaseManualFix) => {
            let score = 0
            for (const val of Object.values(row)) {
              if (val === null || val === undefined) continue
              const strVal = (
                typeof val === 'object' ? JSON.stringify(val) : (val as any).toString()
              ).toLowerCase()
              if (strVal === query) {
                score += 10
              } else if (strVal.startsWith(query)) {
                score += 5
              } else if (strVal.includes(query)) {
                score += 1
              }
            }
            return { row, score }
          })
          .filter((item) => item.score > 0)

        return scoredRows
          .sort((a, b) => {
            if (b.score !== a.score) {
              return b.score - a.score
            }
            return (
              new Date(b.row.created_on || 0).getTime() - new Date(a.row.created_on || 0).getTime()
            )
          })
          .map((item) => item.row)
      }

      return [...rows].sort((a, b) => {
        return new Date(b.created_on || 0).getTime() - new Date(a.created_on || 0).getTime()
      })
    },
    getColumns(tableName: TableName): QTableColumn[] {
      const commonCols: QTableColumn[] = [
        {
          name: 'actions',
          label: 'Actions',
          align: 'center',
          style: 'width: 80px',
          field: 'actions'
        },
        {
          name: 'created_on',
          label: 'Created On',
          field: 'created_on',
          align: 'left',
          sortable: true,
          format: (val: string) => this.formatDate(val)
        },
        {
          name: 'created_by',
          label: 'Created By',
          field: 'created_by',
          align: 'left',
          sortable: true
        }
      ]

      let tableCols: QTableColumn[] = []
      switch (tableName) {
        case 'do_not_merge':
          tableCols = [
            {
              name: 'address1',
              label: 'Address 1',
              field: 'address1',
              align: 'left',
              sortable: true
            },
            {
              name: 'address2',
              label: 'Address 2',
              field: 'address2',
              align: 'left',
              sortable: true
            }
          ]
          break
        case 'excluded_urls':
          tableCols = [{ name: 'url', label: 'URL', field: 'url', align: 'left', sortable: true }]
          break
        case 'prioritize_listing':
          tableCols = [
            {
              name: 'building_id',
              label: 'Building ID',
              field: 'building_id',
              align: 'left',
              sortable: true
            },
            {
              name: 'listing_source',
              label: 'Listing Source',
              field: 'listing_source',
              align: 'left',
              sortable: true
            }
          ]
          break
        case 'address_rename':
          tableCols = [
            {
              name: 'building_name_pattern',
              label: 'Name Pattern',
              field: 'building_name_pattern',
              align: 'left',
              sortable: true
            },
            {
              name: 'address_pattern',
              label: 'Address Pattern',
              field: 'address_pattern',
              align: 'left',
              sortable: true
            },
            {
              name: 'renamed_address',
              label: 'Renamed Address',
              field: 'renamed_address',
              align: 'left',
              sortable: true
            }
          ]
          break
        case 'allowed_website_domains':
          tableCols = [
            {
              name: 'domain_pattern',
              label: 'Domain Pattern',
              field: 'domain_pattern',
              align: 'left',
              sortable: true
            }
          ]
          break
        case 'building_overrides':
          tableCols = [
            { name: 'id', label: 'Building ID', field: 'id', align: 'left', sortable: true },
            {
              name: 'building_name',
              label: 'Building Name',
              field: 'building_name',
              align: 'left',
              sortable: true
            },
            {
              name: 'street_address',
              label: 'Street Address',
              field: 'street_address',
              align: 'left',
              sortable: true
            },
            {
              name: 'street_address_alias',
              label: 'Address Aliases',
              field: (row: BuildingOverride) =>
                Array.isArray(row.street_address_alias) ? row.street_address_alias.join(', ') : '',
              align: 'left',
              sortable: true
            },
            { name: 'city', label: 'City', field: 'city', align: 'left', sortable: true },
            { name: 'state', label: 'State', field: 'state', align: 'left', sortable: true },
            {
              name: 'zip_code',
              label: 'Zip Code',
              field: 'zip_code',
              align: 'left',
              sortable: true
            },
            {
              name: 'number_units',
              label: 'Units',
              field: 'number_units',
              align: 'center',
              sortable: true
            },
            {
              name: 'year_built',
              label: 'Year Built',
              field: 'year_built',
              align: 'center',
              sortable: true
            },
            {
              name: 'management_company',
              label: 'Management Co.',
              field: 'management_company',
              align: 'left',
              sortable: true
            },
            {
              name: 'force_override',
              label: 'Force Override',
              field: (row: BuildingOverride) => (row.force_override ? 'Yes' : 'No'),
              align: 'center',
              sortable: true
            }
          ]
          break
        case 'do_merge_addresses':
          tableCols = [
            { name: 'address', label: 'Address', field: 'address', align: 'left', sortable: true }
          ]
          break
        case 'sightmap_urls':
          tableCols = [
            {
              name: 'widget_hash_id',
              label: 'Widget Hash ID',
              field: 'widget_hash_id',
              align: 'left',
              sortable: true
            },
            {
              name: 'building_id',
              label: 'Building ID',
              field: 'building_id',
              align: 'left',
              sortable: true
            },
            {
              name: 'origin_url',
              label: 'Origin URL',
              field: 'origin_url',
              align: 'left',
              sortable: true
            },
            {
              name: 'building_name',
              label: 'Building Name',
              field: 'building_name',
              align: 'left',
              sortable: true
            },
            {
              name: 'street_address',
              label: 'Street Address',
              field: 'street_address',
              align: 'left',
              sortable: true
            },
            { name: 'city', label: 'City', field: 'city', align: 'left', sortable: true },
            { name: 'state', label: 'State', field: 'state', align: 'left', sortable: true },
            {
              name: 'zip_code',
              label: 'Zip Code',
              field: 'zip_code',
              align: 'left',
              sortable: true
            },
            { name: 'lat', label: 'Lat', field: 'lat', align: 'center', sortable: true },
            { name: 'lon', label: 'Lon', field: 'lon', align: 'center', sortable: true }
          ]
          break
        case 'proposed_edits':
          tableCols = [
            {
              name: 'table_name',
              label: 'Table',
              field: 'table_name',
              align: 'left',
              sortable: true
            },
            { name: 'action', label: 'Action', field: 'action', align: 'left', sortable: true },
            {
              name: 'data_summary',
              label: 'Proposed Data',
              field: (row: ProposedEdit) => {
                try {
                  const data = JSON.parse(row.data) as Record<string, unknown>
                  return Object.entries(data)
                    .map(([k, v]) => `${k}: ${String(v)}`)
                    .join(', ')
                } catch {
                  return row.data
                }
              },
              align: 'left',
              style:
                'max-width: 400px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap'
            }
          ]
          break
      }
      return [...tableCols, ...commonCols]
    },
    getFields(tableName: TableName): Field[] {
      switch (tableName) {
        case 'do_not_merge': {
          return [
            {
              name: 'address1',
              label: 'Address 1 (Required)',
              type: 'string',
              required: true
            },
            {
              name: 'address2',
              label: 'Address 2 (Required)',
              type: 'string',
              required: true
            }
          ]
        }
        case 'excluded_urls': {
          return [{ name: 'url', label: 'URL (Required)', type: 'string', required: true }]
        }
        case 'prioritize_listing': {
          return [
            {
              name: 'building_id',
              label: 'Building ID (Required)',
              type: 'string',
              required: true
            },
            {
              name: 'listing_source',
              label: 'Listing Source (Required)',
              type: 'select',
              required: true,
              options: [
                'American Campus',
                'Apartment List',
                'Apartments',
                'Appfolio',
                'Entrata',
                'GooglePlace',
                'Knock',
                'Mri',
                'OnSite',
                'RealPage',
                'RentCafe',
                'SightMap',
                'StreetEasy',
                'Yardi',
                'Zillow',
                'Zumper'
              ]
            }
          ]
        }
        case 'address_rename': {
          return [
            { name: 'building_name_pattern', label: 'Building Name Pattern', type: 'string' },
            {
              name: 'address_pattern',
              label: 'Address Pattern (Required)',
              type: 'string',
              required: true
            },
            {
              name: 'renamed_address',
              label: 'Renamed Address (Required)',
              type: 'string',
              required: true
            }
          ]
        }
        case 'allowed_website_domains': {
          return [
            {
              name: 'domain_pattern',
              label: 'Domain Pattern (Required)',
              type: 'string',
              required: true
            }
          ]
        }
        case 'building_overrides': {
          return [
            { name: 'id', label: 'Building ID (Required)', type: 'string', required: true },
            { name: 'building_name', label: 'Building Name', type: 'string' },
            { name: 'street_address', label: 'Street Address', type: 'string' },
            { name: 'street_address_alias', label: 'Address Aliases', type: 'array' },
            { name: 'city', label: 'City', type: 'string' },
            { name: 'state', label: 'State', type: 'string' },
            { name: 'zip_code', label: 'Zip Code', type: 'string' },
            { name: 'number_units', label: 'Number of Units', type: 'number' },
            { name: 'year_built', label: 'Year Built', type: 'number' },
            { name: 'management_company', label: 'Management Company', type: 'string' },
            {
              name: 'force_override',
              label: 'Force Override',
              type: 'boolean',
              tooltip:
                "If force override is true then we will force the building to that value, and if false we only set to that value if it's otherwise null."
            }
          ]
        }
        case 'do_merge_addresses': {
          return [{ name: 'address', label: 'Address', type: 'string' }]
        }
        case 'sightmap_urls': {
          return [
            { name: 'building_id', label: 'Building ID', type: 'string', previewTrigger: true },
            {
              name: 'widget_hash_id',
              label: 'Widget Hash ID (Required)',
              type: 'string',
              required: true
            },
            {
              name: 'origin_url',
              label: 'Origin URL (Required)',
              type: 'string',
              required: true
            },
            {
              name: 'building_name',
              label: 'Building Name (Required)',
              type: 'string',
              required: true
            },
            {
              name: 'street_address',
              label: 'Street Address (Required)',
              type: 'string',
              required: true
            },
            { name: 'city', label: 'City (Required)', type: 'string', required: true },
            { name: 'state', label: 'State (Required)', type: 'string', required: true },
            { name: 'zip_code', label: 'Zip Code (Required)', type: 'string', required: true },
            { name: 'lat', label: 'Latitude (Required)', type: 'number', required: true },
            { name: 'lon', label: 'Longitude (Required)', type: 'number', required: true }
          ]
        }
        case 'proposed_edits': {
          return [
            {
              name: 'data',
              label: 'Data (JSON)',
              type: 'string',
              required: true,
              textarea: true
            }
          ]
        }
        default: {
          return []
        }
      }
    },
    openAddDialog(tableName: TableName) {
      this.isEditing = false
      this.isBulkMode = false
      this.bulkSideA = ''
      this.bulkSideB = ''
      this.form = {}
      this.clearFormErrors()

      if (tableName === 'building_overrides') {
        this.form.force_override = false
      }

      if (this.property) {
        if (
          tableName === 'do_not_merge' ||
          tableName === 'address_rename' ||
          tableName === 'building_overrides'
        ) {
          this.form.id = this.property.id
        }
        if (tableName === 'prioritize_listing') {
          this.form.building_id = this.property.id
        }
        if (tableName === 'sightmap_urls') {
          this.form.building_id = this.property.id
        }
      }
      this.showFormDialog = true
    },
    openEditDialog(row: BaseManualFix) {
      this.isEditing = true
      this.isBulkMode = false
      this.editingRow = row
      this.form = { ...row }
      this.clearFormErrors()
      this.showFormDialog = true
    },
    getWhereClause(tableName: TableName, row: BaseManualFix): Record<string, any> {
      return this.manualEditsStore.getWhereClause(tableName, row)
    },
    cleanFormData(data: Record<string, any>, tableName: TableName) {
      // Clean the form data by removing empty strings for optional fields
      const cleaned = { ...data }
      const fields = this.getFields(tableName)

      for (const field of fields) {
        const value = cleaned[field.name]

        // For number fields, convert empty strings to null or remove them
        if (field.type === 'number') {
          if (value === '' || value === null || value === undefined) {
            delete cleaned[field.name]
          }
        }

        // For optional string fields, remove empty strings
        if (field.type === 'string' && !field.required) {
          if (value === '' || value === null || value === undefined) {
            delete cleaned[field.name]
          }
        }
      }

      // Convert listing_source to lowercase for backend compatibility
      if (cleaned.listing_source && typeof cleaned.listing_source === 'string') {
        cleaned.listing_source = cleaned.listing_source.toLowerCase()
      }

      return cleaned
    },
    validateForm(tableName: TableName) {
      this.clearFormErrors()
      const errors: string[] = []
      const fields = this.getFields(tableName)

      for (const field of fields) {
        // Skip validation for optional or non-required fields
        if (field.type === 'boolean' || field.type === 'array') {
          continue
        }

        const value = this.form[field.name]
        const fieldLabel = field.label.replace(' (Required)', '')

        // Check if field is empty
        if (
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '')
        ) {
          // Only require fields that are marked as required or are essential
          if (
            field.required ||
            field.name === 'address1' ||
            field.name === 'address2' ||
            field.name === 'url' ||
            field.name === 'building_id' ||
            field.name === 'listing_source' ||
            field.name === 'address' ||
            field.name === 'address_pattern' ||
            field.name === 'renamed_address' ||
            field.name === 'domain_pattern'
          ) {
            errors.push(`${fieldLabel} is required`)
            this.fieldErrors[field.name] = `${fieldLabel} is required`
          }
        }
      }

      if (this.activeTab === 'do_not_merge') {
        const addr1 = (this.form.address1 as string | undefined)?.trim().toLowerCase()
        const addr2 = (this.form.address2 as string | undefined)?.trim().toLowerCase()
        if (addr1 && addr2 && addr1 === addr2) {
          errors.push(
            'Address 1 and Address 2 cannot be the same. An address cannot be paired with itself.'
          )
          this.fieldErrors['address1'] = 'Cannot be the same as Address 2'
          this.fieldErrors['address2'] = 'Cannot be the same as Address 1'
        }
      }

      if (tableName === 'proposed_edits') {
        if (this.form.data) {
          try {
            JSON.parse(this.form.data as string)
          } catch {
            errors.push('Data must be a valid JSON string')
            this.fieldErrors['data'] = 'Invalid JSON'
          }
        }
      }

      if (errors.length > 0) {
        this.formErrors = errors
        this.$q.notify({
          type: 'negative',
          message: `Please fix validation errors (${errors.length} issue${errors.length > 1 ? 's' : ''})`
        })
        return false
      }

      return true
    },
    async saveEntry() {
      const tableName = this.activeTab
      if (tableName === 'proposed_edits' && !this.isEditing) {
        this.$q.notify({
          type: 'warning',
          message: 'Cannot manually insert into proposed_edits dataset'
        })
        return
      }
      let saveSuccessful = false
      try {
        if (this.isEditing) {
          // Validate form before updating
          if (!this.validateForm(tableName)) {
            return
          }

          if (tableName === 'do_not_merge') {
            this.form.id =
              `${(this.form.address1 as string) || ''}${(this.form.address2 as string) || ''}`
                .toLowerCase()
                .replace(/\s+/g, '') || `manual_${Date.now()}`
          }

          // Construct updates object using only fields defined for this table
          let updates: Record<string, any> = {}
          const fields = this.getFields(tableName)
          for (const field of fields) {
            if (this.form[field.name] !== undefined) {
              updates[field.name] = this.form[field.name]
            }
          }
          if (tableName === 'do_not_merge' && this.form.id) {
            updates.id = this.form.id
          }

          // Clean empty optional fields
          updates = this.cleanFormData(updates, tableName)

          await this.manualEditsStore.saveEntry(tableName, true, updates, this.editingRow)

          this.$q.notify({
            type: 'positive',
            message: this.canEdit
              ? 'Entry updated successfully'
              : 'Edit proposal submitted successfully'
          })
          saveSuccessful = true
        } else if (this.isBulkMode && tableName === 'do_not_merge') {
          this.clearFormErrors()
          const setA = this.bulkSideA
            .split('\n')
            .map((s: string) => s.trim())
            .filter(Boolean)
          const setB = this.bulkSideB
            .split('\n')
            .map((s: string) => s.trim())
            .filter(Boolean)

          if (setA.length === 0 || setB.length === 0) {
            this.formErrors = ['Please enter items in both Side A and Side B']
            this.$q.notify({
              type: 'warning',
              message: 'Please enter items in both Side A and Side B'
            })
            return
          }

          // Check for overlapping values between Side A and Side B (case-insensitive)
          const setALower = setA.map((a: string) => a.toLowerCase())
          const setBLower = setB.map((b: string) => b.toLowerCase())
          const overlapping = setA.filter((a: string, idx: number) =>
            setBLower.includes(setALower[idx])
          )

          if (overlapping.length > 0) {
            const overlapList =
              overlapping.slice(0, 5).join(', ') +
              (overlapping.length > 5 ? `, and ${overlapping.length - 5} more` : '')
            this.formErrors = [
              `Cannot create do-not-merge pairs: The following addresses appear in both Side A and Side B: ${overlapList}`
            ]
            this.$q.notify({
              type: 'negative',
              message: `Validation Error: ${overlapping.length} address(es) appear in both sides. An address cannot be paired with itself.`
            })
            return
          }

          for (const a of setA) {
            for (const b of setB) {
              const id = `${a}${b}`.toLowerCase().replace(/\s+/g, '')
              await this.manualEditsStore.insertDoNotMerge({ id, address1: a, address2: b })
            }
          }
          this.$q.notify({
            type: 'positive',
            message: this.canEdit
              ? `Created ${setA.length * setB.length} do-not-merge pairs`
              : `Submitted proposal for ${setA.length * setB.length} pairs`
          })
          saveSuccessful = true
        } else if (tableName === 'do_merge_addresses' && !this.isEditing) {
          const addresses = this.bulkSideA
            .split('\n')
            .map((s: string) => s.trim())
            .filter(Boolean)

          if (addresses.length < 2) {
            this.$q.notify({
              type: 'warning',
              message: 'Please enter at least two addresses to create a merge group'
            })
            return
          }

          const groupId = `group_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`
          for (const address of addresses) {
            await this.manualEditsStore.insertDoMergeAddress({
              merge_group_id: groupId,
              address: address
            })
          }
          this.$q.notify({
            type: 'positive',
            message: this.canEdit
              ? `Created new group with ${addresses.length} addresses`
              : 'Merge proposal submitted successfully'
          })
          saveSuccessful = true
        } else if (tableName === 'sightmap_urls') {
          // Clear previous errors
          this.clearFormErrors()

          // Validate all required fields
          const requiredFields = [
            { name: 'widget_hash_id', label: 'Widget Hash ID' },
            { name: 'origin_url', label: 'Origin URL' },
            { name: 'building_name', label: 'Building Name' },
            { name: 'street_address', label: 'Street Address' },
            { name: 'city', label: 'City' },
            { name: 'state', label: 'State' },
            { name: 'zip_code', label: 'Zip Code' },
            { name: 'lat', label: 'Latitude' },
            { name: 'lon', label: 'Longitude' }
          ]

          const errors: string[] = []
          for (const field of requiredFields) {
            const value = this.form[field.name]
            if (
              value === null ||
              value === undefined ||
              (typeof value === 'string' && value.trim() === '')
            ) {
              errors.push(`${field.label} is required`)
              this.fieldErrors[field.name] = `${field.label} is required`
            }
          }

          if (errors.length > 0) {
            this.formErrors = errors
            this.$q.notify({
              type: 'negative',
              message: `Please fill in all required fields (${errors.length} missing)`
            })
            return
          }

          // Trim string fields
          if (this.form.widget_hash_id) {
            this.form.widget_hash_id = (this.form.widget_hash_id as string).trim()
          }
          if (this.form.origin_url) {
            this.form.origin_url = (this.form.origin_url as string).trim()
          }
          if (this.form.building_name) {
            this.form.building_name = (this.form.building_name as string).trim()
          }
          if (this.form.street_address) {
            this.form.street_address = (this.form.street_address as string).trim()
          }
          if (this.form.city) {
            this.form.city = (this.form.city as string).trim()
          }
          if (this.form.state) {
            this.form.state = (this.form.state as string).trim()
          }
          if (this.form.zip_code) {
            this.form.zip_code = (this.form.zip_code as string).trim()
          }

          if (!this.isEditing) {
            // Check if widget_hash_id already exists (case-insensitive, trimmed comparison)
            const normalizedWidgetId = (this.form.widget_hash_id as string).toLowerCase()
            const existing = (this.allData.sightmap_urls as SightmapUrl[]).find(
              (r) =>
                r.widget_hash_id && r.widget_hash_id.trim().toLowerCase() === normalizedWidgetId
            )

            if (existing) {
              // Update existing entry
              const where = this.getWhereClause('sightmap_urls', existing)
              const updates = { ...this.form }
              delete updates.created_on
              delete updates.created_by
              await this.manualEditsStore.updateSightmapUrl({
                where: where as { widget_hash_id: string },
                updates
              })
              this.$q.notify({
                type: 'positive',
                message: this.canEdit
                  ? 'Existing sightmap entry updated'
                  : 'Update proposal submitted'
              })
              saveSuccessful = true
            } else {
              // Insert new entry
              await this.manualEditsStore.insertSightmapUrl(this.form as SightmapUrl)
              this.$q.notify({
                type: 'positive',
                message: this.canEdit
                  ? 'New sightmap entry created'
                  : 'New sightmap proposal submitted'
              })
              saveSuccessful = true
            }
          } else {
            // Edit mode
            const where = this.getWhereClause('sightmap_urls', this.editingRow)
            const updates = { ...this.form }
            delete updates.created_on
            delete updates.created_by
            await this.manualEditsStore.updateSightmapUrl({
              where: where as { widget_hash_id: string },
              updates
            })
            this.$q.notify({
              type: 'positive',
              message: this.canEdit
                ? 'Sightmap entry updated successfully'
                : 'Sightmap update proposal submitted'
            })
            saveSuccessful = true
          }
        } else {
          // Validate form before inserting (for non-bulk, non-sightmap forms)
          if (!this.validateForm(tableName)) {
            return
          }

          if (tableName === 'do_not_merge' && !this.form.id) {
            this.form.id =
              `${(this.form.address1 as string) || ''}${(this.form.address2 as string) || ''}`
                .toLowerCase()
                .replace(/\s+/g, '') || `manual_${Date.now()}`
          }
          if (tableName === 'address_rename' && !this.form.id) {
            this.form.id = `rename_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`
          }
          if (tableName === 'allowed_website_domains' && !this.form.id) {
            this.form.id = Date.now()
          }
          // Clean empty optional fields before inserting
          const cleanedData = this.cleanFormData(this.form, tableName)
          await this.manualEditsStore.saveEntry(tableName, false, cleanedData)

          this.$q.notify({
            type: 'positive',
            message: this.canEdit
              ? 'Entry added successfully'
              : 'Entry proposal submitted successfully'
          })
          saveSuccessful = true
        }
        this.clearFormErrors()
        this.showFormDialog = false
      } catch (error) {
        console.error('Error saving entry:', error)
        this.$q.notify({ type: 'negative', message: 'Failed to save entry' })
      } finally {
        // Only refresh if save was successful
        if (saveSuccessful) {
          // Wait for backend to complete processing before refreshing (BigQuery can be slow)
          await new Promise((resolve) => setTimeout(resolve, 2500))
          await this.fetchAllData()
          this.$emit('refresh')
        }
      }
    },
    confirmDelete(row: any) {
      this.deletingRow = row
      this.showDeleteDialog = true
    },
    async deleteEntry() {
      const tableName = this.activeTab
      try {
        await this.manualEditsStore.deleteEntry(tableName, this.deletingRow)

        this.$q.notify({
          type: 'positive',
          message: this.canEdit
            ? 'Entry deleted successfully'
            : 'Deletion proposal submitted successfully'
        })
        this.showDeleteDialog = false
      } catch (error) {
        console.error('Error deleting entry:', error)
        this.$q.notify({ type: 'negative', message: 'Failed to delete entry' })
      } finally {
        // Wait for backend to complete processing before refreshing (BigQuery can be slow)
        await new Promise((resolve) => setTimeout(resolve, 2500))
        await this.fetchAllData()
        this.$emit('refresh')
      }
    },
    async approveProposal(proposalId: string) {
      try {
        await this.manualEditsStore.approveProposedEdit(proposalId)
        this.$q.notify({ type: 'positive', message: 'Proposal approved and made live' })
        await this.fetchAllData()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error approving proposal:', error)
        this.$q.notify({ type: 'negative', message: 'Failed to approve proposal' })
      }
    },
    confirmDeleteProposal(row: any) {
      this.$q
        .dialog({
          title: 'Delete Proposal',
          message: 'Are you sure you want to delete this proposal?',
          cancel: true,
          persistent: true
        })
        .onOk(() => {
          void (async () => {
            try {
              await this.manualEditsStore.deleteProposedEdit(row.id as string)
              this.$q.notify({ type: 'positive', message: 'Proposal deleted' })
              await this.fetchAllData()
            } catch (error) {
              console.error('Error deleting proposal:', error)
              this.$q.notify({ type: 'negative', message: 'Failed to delete proposal' })
            }
          })()
        })
    }
  }
})
</script>

<style scoped>
.q-tab-panel {
  display: flex;
  flex-direction: column;
}

.merge-groups-container {
  max-height: calc(90vh - 200px);
  overflow-y: auto;
}

.merge-group {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.merge-group-header {
  border-bottom: 1px solid #e0e0e0;
  font-weight: 500;
}
</style>
